import React, { useEffect, useMemo, useState } from 'react';
import { constructorInstance } from 'common/api/constructor/constructor-instance';
import {
    processOnBonusBrands,
    ConstructorReturnObject,
    constructorToBrand,
} from 'common/api/constructor/util';
import { useFirebaseConfigValues, firebaseValueAsString } from 'hooks/useFirebaseConfigValues';
import { isAffiliateOnlyBrand } from 'common/utils/affiliate';
import { parseEnrollPreviewBrandIdsFromRemoteConfig } from './enrollPreviewBrandIds';
import Skeleton from 'react-loading-skeleton';

const MAX_BRAND_IDS = 100;
const MAX_RESULTS_PER_PAGE = 200;

const ENROLL_BRANDS_STRIP_GRADIENT =
    'linear-gradient(180deg, rgba(225, 241, 247, 0) 65%, #E1F1F7 90%)';

export type EnrollPreviewBrand = ReturnType<typeof constructorToBrand>[number];

/** Matches brand card earn display: affiliate brands use affiliateRebate; gift cards use maxRebate. */
export function getEnrollPreviewBrandEarningsRate(brand: EnrollPreviewBrand): number | undefined {
    if (isAffiliateOnlyBrand(String(brand.id))) {
        return brand.affiliateBonus ?? brand.affiliateRebate;
    }

    return brand.maxRebate;
}

export async function fetchBrandsByItemIds(brandIds: string[]): Promise<EnrollPreviewBrand[]> {
    if (!brandIds.length) {
        return [];
    }

    const limitedBrandIds = brandIds.slice(0, MAX_BRAND_IDS);
    const itemIds = limitedBrandIds.map((id) => `${id}`);

    const searchResult = await constructorInstance.browse.getBrowseResultsForItemIds(itemIds, {
        resultsPerPage: Math.min(MAX_RESULTS_PER_PAGE, Math.max(itemIds.length, 1)),
    });
    const allResults = (searchResult.response?.results || []) as ConstructorReturnObject[];

    const validBrands = processOnBonusBrands(allResults, false);
    const mapped = constructorToBrand(validBrands || []);

    const byId = new Map(mapped.map((b: { id?: string | number }) => [String(b.id), b] as const));
    return limitedBrandIds
        .map((id) => byId.get(`${id}`))
        .filter((b): b is EnrollPreviewBrand => Boolean(b));
}

export function useEnrollPreviewBrands(brandIds?: string[]) {
    const remoteRaw = useFirebaseConfigValues(
        'enrollment_brand_ids_web',
        '',
        firebaseValueAsString
    );

    const idsFromRemote = useMemo(
        () => parseEnrollPreviewBrandIdsFromRemoteConfig(remoteRaw),
        [remoteRaw]
    );

    const idsKey = (brandIds ?? idsFromRemote).join(',');
    const [brands, setBrands] = useState<EnrollPreviewBrand[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<Error | null>(null);

    useEffect(() => {
        const ids = idsKey.split(',').filter(Boolean);

        async function load() {
            setLoading(true);
            setError(null);
            try {
                const next = await fetchBrandsByItemIds(ids);
                setBrands(next);
            } catch (e) {
                setError(e instanceof Error ? e : new Error(String(e)));
                setBrands([]);
            } finally {
                setLoading(false);
            }
        }

        load();
    }, [idsKey]);

    return { brands, loading, error };
}

const BrandsScroller = () => {
    const { brands, loading, error } = useEnrollPreviewBrands();

    if (loading) {
        return <Skeleton className="w-full h-[100px]" />;
    }
    if (error) {
        return (
            <div className="text-red-600 text-sm" role="alert">
                {error.message}
            </div>
        );
    }

    const renderBrandTile = (b: EnrollPreviewBrand, keySuffix: string, isDuplicateRow: boolean) => (
        <div
            key={`${String(b.id)}-${keySuffix}`}
            className="flex w-[120px] shrink-0 flex-col items-center gap-1 text-center text-[18px] text-grey-1"
        >
            <div className="flex h-[100px] w-[100px] shrink-0 items-center justify-center overflow-hidden rounded ">
                <img
                    src={b.imageUrl}
                    alt={isDuplicateRow ? '' : b.name ?? ''}
                    className="max-h-full max-w-full object-contain"
                    width={100}
                    height={100}
                />
            </div>
            <div className="whitespace-nowrap">
                Earn{' '}
                <strong className="text-dark-blue font-semibold">
                    {getEnrollPreviewBrandEarningsRate(b)}%
                </strong>
            </div>
        </div>
    );

    return (
        <div
            className="relative left-1/2 w-screen max-w-none -translate-x-1/2 overflow-hidden"
            style={{ background: ENROLL_BRANDS_STRIP_GRADIENT }}
            aria-label="Featured brands"
        >
            <div className="flex w-max animate-enroll-brand-marquee motion-reduce:animate-none pb-[72px] mt-4">
                {[0, 1].map((copy) => (
                    <div
                        key={copy}
                        className="flex shrink-0 flex-row items-start gap-4 lg:gap-8 pr-4"
                        aria-hidden={copy === 1}
                    >
                        {brands.map((b) => renderBrandTile(b, `c${copy}`, copy === 1))}
                    </div>
                ))}
            </div>
        </div>
    );
};

export default BrandsScroller;
